import { apiClient } from '@/api/client';

/**
 * Historique des imports de l'assistant d'import V2 : sessions par école,
 * détail élément par élément (uuid V2 + payload JSON) et fichiers sources
 * conservés (re-téléchargeables).
 */

export type ImportSessionStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type ImportRecordStatus = 'created' | 'updated' | 'skipped' | 'failed';

export interface ImportSessionFileMeta {
  id: number;
  logicalName: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ImportStepSummary {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
  warnings: string[];
}

/** Analyse rédigée du rapport (Gemini, ou repli déterministe). */
export interface ImportAiAnalysis {
  executiveSummary: string;
  overallStatus: 'on_track' | 'attention' | 'critical';
  stepAnalyses: Array<{ stepId: string; analysis: string; alerts?: string[] }>;
  failedHighlights: Array<{
    scope: string;
    detail: string;
    severity: 'info' | 'warning' | 'critical';
  }>;
  skippedSummary: string;
  recommendations: string[];
  conclusion: string;
}

export interface ImportSessionSummary {
  id: number;
  status: ImportSessionStatus;
  createdBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  targetSchoolPeriodId: string | null;
  targetSchoolPeriodLabel: string | null;
  totalItems: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  stepsSummary: Record<string, ImportStepSummary> | null;
  aiAnalysis: ImportAiAnalysis | null;
  aiGenerated: boolean;
  reportGeneratedAt: string | null;
  files: ImportSessionFileMeta[];
}

export interface ImportSessionDetail extends ImportSessionSummary {
  /** stepId → status → nombre d'éléments tracés */
  recordCounts: Record<string, Record<string, number>>;
}

export interface ImportRecordRow {
  id: number;
  stepId: string;
  entityType: string;
  label: string;
  status: ImportRecordStatus;
  v2Uuid: string | null;
  payload: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  errorMessage: string | null;
}

export interface ImportRecordsPage {
  items: ImportRecordRow[];
  total: number;
  page: number;
  limit: number;
}

const base = (code: string) => `/api/schools/${code}/v2-import/sessions`;

/** Ouvre une session d'historique et y archive les fichiers sources. */
export async function createImportSession(
  code: string,
  files: Record<string, File>,
  opts: { targetSchoolPeriodId?: string; targetSchoolPeriodLabel?: string },
): Promise<number> {
  const formData = new FormData();
  Object.entries(files).forEach(([logicalName, file]) => {
    formData.append(logicalName, file, file.name || `${logicalName}.csv`);
  });
  if (opts.targetSchoolPeriodId) {
    formData.append('targetSchoolPeriodId', opts.targetSchoolPeriodId);
  }
  if (opts.targetSchoolPeriodLabel) {
    formData.append('targetSchoolPeriodLabel', opts.targetSchoolPeriodLabel);
  }

  const res = await apiClient.post<{ id: number }>(base(code), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.id;
}

/** Clôture la session (statut final calculé côté serveur si échecs). */
export async function finishImportSession(
  code: string,
  sessionId: number,
  status: 'completed' | 'failed',
): Promise<ImportSessionSummary> {
  const res = await apiClient.post<ImportSessionSummary>(
    `${base(code)}/${sessionId}/finish`,
    { status },
  );
  return res.data;
}

/**
 * Génère (ou régénère) le rapport rédigé de l'import (analyse IA + repli
 * déterministe) — stocké sur la session, consultable ensuite à tout moment.
 */
export async function generateImportReport(
  code: string,
  sessionId: number,
): Promise<ImportSessionSummary> {
  const res = await apiClient.post<ImportSessionSummary>(`${base(code)}/${sessionId}/report`);
  return res.data;
}

export async function listImportSessions(code: string): Promise<ImportSessionSummary[]> {
  const res = await apiClient.get<{ items: ImportSessionSummary[] }>(base(code));
  return res.data.items ?? [];
}

export async function getImportSession(
  code: string,
  sessionId: number,
): Promise<ImportSessionDetail> {
  const res = await apiClient.get<ImportSessionDetail>(`${base(code)}/${sessionId}`);
  return res.data;
}

export async function listImportRecords(
  code: string,
  sessionId: number,
  opts?: { step?: string; status?: string; q?: string; page?: number; limit?: number },
): Promise<ImportRecordsPage> {
  const params = new URLSearchParams();
  if (opts?.step) params.append('step', opts.step);
  if (opts?.status) params.append('status', opts.status);
  if (opts?.q) params.append('q', opts.q);
  if (opts?.page) params.append('page', String(opts.page));
  if (opts?.limit) params.append('limit', String(opts.limit));

  const res = await apiClient.get<ImportRecordsPage>(
    `${base(code)}/${sessionId}/records?${params.toString()}`,
  );
  return res.data;
}

/** Déclenche le téléchargement navigateur d'un blob. */
function triggerDownload(data: Blob, filename: string): void {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** Re-télécharge UN fichier source de l'import. */
export async function downloadImportFile(
  code: string,
  sessionId: number,
  file: ImportSessionFileMeta,
): Promise<void> {
  const res = await apiClient.get(`${base(code)}/${sessionId}/files/${file.id}`, {
    responseType: 'blob',
  });
  triggerDownload(new Blob([res.data]), file.filename);
}

/** Re-télécharge TOUS les fichiers sources de l'import (ZIP). */
export async function downloadAllImportFiles(
  code: string,
  sessionId: number,
): Promise<void> {
  const res = await apiClient.get(`${base(code)}/${sessionId}/files`, {
    responseType: 'blob',
  });
  triggerDownload(new Blob([res.data]), `import-${code}-${sessionId}.zip`);
}
